/* eslint-disable default-case */
import { Block } from 'slate';
import { getEventTransfer } from 'slate-react'

import { NODE_TYPE as NT } from 'shared/constants/slate';

import {
  onlyInTopLevelParagraph,
  onlyCollapsedInTopLevelParagraphAndEmpty,
  makeNodeProps,
} from '../utils';


// Copypasta from `slate-drop-or-paste-images` plus
// modifications, mainly providing defaultHandler.


const re = /^(data:)([\w/+]+);(charset=[\w-]+|base64).*,(.*)/gi;
const isDataUri = data => (data && re.test(data)) === true;

const hasArrayBufferView = () => {
  return new Blob([new window.Uint8Array(100)]).size === 100
}

const dataUriToBlob = (uri) => {
  const data = uri.split(',')[1]
  const bytes = atob(data)
  const buffer = new window.ArrayBuffer(bytes.length)
  let array = new window.Uint8Array(buffer)

  for (let i = 0; i < bytes.length; i++) {
    array[i] = bytes.charCodeAt(i)
  }

  if (!hasArrayBufferView()) {
    array = buffer
  }

  const type = uri.split('')[0].slice(5)
  const blob = new Blob([array], { type })

  // COMPAT: ???
  blob.slice = blob.slice || blob.webkitSlice

  return blob
}


const imageToDataUri = (url, callback) => {
  const canvas = document.createElement('canvas')
  const img = document.createElement('img')

  if (!canvas.getContext) {
    return setTimeout(callback, 0, new Error('Canvas is not supported.'))
  }

  img.onload = () => {
    const ctx = canvas.getContext('2d')
    canvas.width = img.width
    canvas.height = img.height
    ctx.drawImage(img, 0, 0)
    const dataUri = canvas.toDataURL('image/png')
    callback(null, dataUri)
  }

  img.ononerror = () => {
    callback(new Error('Failed to load image.'))
  }

  img.setAttribute('crossOrigin', 'anonymous')
  img.src = url
}

const loadImageFile = (url, callback) => {
  if (isDataUri(url)) {
    const file = dataUriToBlob(url)
    setTimeout(() => {
      callback(null, file)
    })
  } else {
    imageToDataUri(url, (err, uri) => {
      const file = dataUriToBlob(uri)
      callback(err, file)
    })
  }
}


// node is optional
const onFileLoadInsertImageSrc = (result, editor, node) => {

  const nodeProps = makeNodeProps({
    type: NT.image,
    isVoid: true,
  }, {
    src: result,
    isReceiving: false,
    error: null,
  });

  // If we have a target node already (say, isReceiving = true),
  // then insert image into that node.
  const { value } = editor;
  const change = value.change();
  if (node) {
    change
      .setNodeByKey(node.key, nodeProps)
      .collapseToStartOfNextBlock()
      .collapseToStartOfNextText()

    return editor.onChange(change);
  }

  // .. otherwise figure out the best way to insert an image below.

  // In an empty paragraph, best to insert above.
  if (onlyCollapsedInTopLevelParagraphAndEmpty(value)) {
    const { startBlock, document: doc } = value;
    const index = doc.nodes.findIndex(n => n.key === startBlock.key);
    const image = Block.create(nodeProps);

    change.insertNodeByKey(doc.key, index, image);
    return editor.onChange(change);
  }

  // In a populated paragraph, just insert and that's it.
  change.insertBlock(nodeProps);
  return editor.onChange(change);
};


const defaultHandler = (change, editor, file) => {
  const { value } = change;

  // For now only allow in top level paragraph..
  if (!onlyInTopLevelParagraph(value)) return change;

  const reader = new FileReader();
  reader.addEventListener('load',
    () => onFileLoadInsertImageSrc(reader.result, editor)
  );
  reader.readAsDataURL(file);
};



const DropOrPasteImage = (options = {}) => {
  const {
    insertImage = defaultHandler,
    extensions,
  } = options;

  const asyncApplyChange = (change, editor, file) => {
    return Promise
      .resolve(insertImage(change, editor, file))
      .then(() => {
        editor.onChange(change)
      })
  }


  const onInsert = (event, change, editor) => {
    const transfer = getEventTransfer(event)

    switch (transfer.type) {
      case 'files': return onInsertFiles(event, change, editor, transfer)
      case 'html': return onInsertHtml(event, change, editor, transfer)
      // case 'text': return onInsertText(event, change, editor, transfer)
    }
  }

  const onInsertFiles = (event, change, editor, transfer) => {
    const { target, files } = transfer

    for (const file of files) {
      if (extensions) {
        const ext = file.type.split('/')[1]
        // const ext = mime.extension(file.type)
        if (!extensions.includes(ext)) continue
      }

      if (target) {
        change.select(target)
      }

      asyncApplyChange(change, editor, file)
    }

    return true
  }

  const onInsertHtml = (event, change, editor, transfer) => {
    const { html, target } = transfer

    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const body = doc.body
    const firstChild = body.firstChild
    if (firstChild.nodeName.toLowerCase() !== 'img') return

    const src = firstChild.src

    if (extensions) {
      // const ext = extname(src).slice(1)
      const ext = src.split('/')[1]
      if (!extensions.includes(ext)) return
    }

    loadImageFile(src, (err, file) => {
      if (err) return
      const c = editor.value.change()
      if (target) c.select(target)
      asyncApplyChange(c, editor, file)
    })

    return true
  }

  // const onInsertText = (event, change, editor, transfer) => {
  //   const { text, target } = transfer
  //
  //   if (!isUrl(text)) return
  //   if (!isImage(text)) return
  //
  //   loadImageFile(text, (err, file) => {
  //     if (err) return
  //     const c = editor.value.change()
  //     if (target) c.select(target)
  //     asyncApplyChange(c, editor, file)
  //   })
  //
  //   return true
  // }

  return {
    onDrop: onInsert,
    onPaste: onInsert,
  }
}


export {
  onFileLoadInsertImageSrc,
  DropOrPasteImage,
}



// WEBPACK FOOTER //
// ./src/components/slate/plugins/DropOrPasteImage.js